import { Box, Paper, Toolbar, Typography } from '@mui/material';
import _ from 'lodash';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import Container from '../Container';
import { FilterPicker, Parameters, Table, TablePagination } from '../controls';
import { useSnackbar } from '../Snackbar';
import EventPreviewMap from '../controls/EventPreviewMap';
import { filterLocally } from '../../data/utilities';
import ChunkedDownloader from './ChunkedDownloader';
import usePaging from './usePaging';

const {
  rowsPerPageOptions,
  events: { eventFilters: { idles: eventFilters = [] } = {} } = {},
} = window.config;
const { extraIdleHeaders } = window.reportHeaders;

const headers = [
  ...extraIdleHeaders,
  {
    label: 'Start Time',
    key: 'startTime',
    type: 'date',
    filter: false,
  },
  {
    label: 'End Time',
    key: 'endTime',
    type: 'date',
    filter: false,
  },
  {
    label: 'Duration (minutes)',
    key: 'durationMinutes',
    serverKey: 'durationSeconds',
    type: 'number',
    filter: false,
    // Footer: _.round(
    //   filteredTrips
    //     .map((trip) => trip.durationMinutes)
    //     .reduce((a, b) => a + b, 0) || 0,
    //   2
    // ).toString(),
  },
  {
    label: 'Home Location',
    key: 'vehicle.homeStation',
    type: 'text',
    filter: true,
  },
  { label: 'Location Name', key: 'location.name', type: 'text', filter: true },
  { label: 'Location Type', key: 'location.type', type: 'text', filter: true },
];

function Event({ entry }) {
  return (
    <Box sx={{ display: 'flex', px: 2, pb: 2 }}>
      <Box sx={{ width: 320, height: 320 }}>
        {entry.point && <EventPreviewMap point={entry.point} mapType="idles" />}
      </Box>
    </Box>
  );
}

export default function Idles() {
  const isLoading = useSelector((state) => state.events.idles.isLoading);
  const error = useSelector((state) => state.events.idles.error);
  const idles = useSelector((state) => state.events.idles.list, _.isEqual);
  const totals = useSelector((state) => state.events.idles.totals);
  const filter = useSelector((state) => state.events.idles.filter, _.isEqual);
  const query = useSelector((state) => state.events.idles.query, _.isEqual);
  const total = useSelector((state) => state.events.idles.total);
  const snackbar = useSnackbar();
  const eventType = 'idles';

  const paging = usePaging({ eventType, headers });

  useEffect(() => {
    if (error) {
      snackbar.notify('error', error);
    }
  }, [error, snackbar]);

  function handleSelectAllClick(event) {
    if (event.target.checked) {
      paging.updateFilter({
        selectedIdentifiers: filterLocally(filter, idles).map(
          (n) => n.identifier
        ),
      });
    } else {
      paging.updateFilter({
        selectedIdentifiers: [],
      });
    }
  }

  function handleSelectClick(id) {
    const index = filter.selectedIdentifiers.indexOf(id);

    if (index === -1) {
      paging.updateFilter({
        selectedIdentifiers: filter.selectedIdentifiers.concat(id),
      });
    } else {
      paging.updateFilter({
        selectedIdentifiers: filter.selectedIdentifiers
          .slice(0, index)
          .concat(filter.selectedIdentifiers.slice(index + 1)),
      });
    }
  }

  // const filteredIdles = filterLocally(filter, idles);

  const filename = 'Vehicle Idles.csv';
  const csvMapping = (idle) => ({
    ...idle,
    startTime: new Date(idle.startTime),
    endTime: new Date(idle.endTime),
    durationMinutes: _.round(idle.durationMinutes || 0.0, 2),
  });

  return (
    <Container title="Idles">
      <Parameters
        onFetch={paging.handleFetch}
        onCancel={paging.handleCancel}
        isFetching={isLoading}
        value={query}
        onChange={paging.handleQueryChange}
        sx={{ mt: 1, width: 264 }}
        vehicle
        driver
        eventFilters={eventFilters}
      />
      <Box
        sx={{
          flex: 1,
          height: 'calc(100vh - 48px)',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <Helmet>
          <title>IR3 | Idles</title>
        </Helmet>
        <Toolbar variant="dense" disableGutters sx={{ p: 1, pb: 0 }}>
          <Typography sx={{ flexGrow: 1 }} variant="subtitle1">
            Idles
          </Typography>
          <FilterPicker
            headers={headers}
            data={idles}
            filter={filter}
            filterValues={filter.filterValues}
            onFilterChange={paging.onFilterChange}
          />
          <ChunkedDownloader
            eventType={eventType}
            filename={filename}
            headers={headers}
            csvMapping={csvMapping}
          />
        </Toolbar>
        <Paper sx={{ m: [0, 1, 1], minWidth: 240 }}>
          <Box sx={isLoading ? { opacity: 0.5 } : undefined}>
            <Table
              styles={{
                tableContainer: {
                  height: 'calc(100vh - 172px)',
                  overflowY: 'scroll',
                },
                table: {
                  minWidth: 750,
                },
              }}
              data={idles}
              headers={[
                {
                  label: '',
                  key: 'expand',
                  type: 'expand',
                  component: Event,
                },
                ...headers,
              ]}
              rowsPerPage={filter.rowsPerPage}
              page={0} //{filter.page} // server-side pagination will fake the page we're on
              keyName="identifier"
              selectedKeys={filter.selectedIdentifiers}
              onSelectAllClick={handleSelectAllClick}
              onSelectClick={handleSelectClick}
              order={filter.order}
              orderBy={filter.orderBy}
              onOrderChange={paging.handleOrderChange}
              onOrderByChange={paging.handleOrderByChange}
              totals={totals}
            />
          </Box>
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={total ?? 0}
            rowsPerPage={filter.rowsPerPage}
            page={filter.page}
            onPageChange={paging.handlePageChange}
            onRowsPerPageChange={paging.handleRowsPerPageChange}
          />
        </Paper>
      </Box>
    </Container>
  );
}
